import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '@fluentui/react-components';
import { LineChart } from '@fluentui/react-charting';
import type { IChartProps, ILineChartPoints } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import { useCSATTrendChartStyles } from './CSATTrendChart.styles';
import type { SurveyTrendPoint } from '@/types/reports';

export interface CSATTrendChartProps {
  data: SurveyTrendPoint[];
  titleKey: string;
}

const BASE_DATE = new Date(2026, 0, 1);
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

export function CSATTrendChart({ data, titleKey }: CSATTrendChartProps) {
  const styles = useCSATTrendChartStyles();
  const { t } = useTranslation('reports');

  const monthLabels = useMemo(() => {
    const map = new Map<number, string>();
    data.forEach((pt, i) => {
      map.set(BASE_DATE.getTime() + i * MONTH_MS, pt.month);
    });
    return map;
  }, [data]);

  const chartData: IChartProps = useMemo(() => {
    const lineChartPoints: ILineChartPoints[] = [
      {
        legend: t('widgets.csatTrendLegend'),
        data: data.map((pt, i) => ({
          x: new Date(BASE_DATE.getTime() + i * MONTH_MS),
          y: pt.csatScore,
          xAxisCalloutData: pt.month,
          yAxisCalloutData: pt.csatScore.toFixed(1),
        })),
        color: tokens.colorBrandForeground1,
      },
    ];
    return { chartTitle: t(titleKey), lineChartData: lineChartPoints };
  }, [data, t, titleKey]);

  const xAxisTickValues = useMemo(
    () => data.map((_, i) => new Date(BASE_DATE.getTime() + i * MONTH_MS)),
    [data]
  );

  return (
    <WidgetContainer title={t(titleKey)}>
      <div className={styles.root} role="figure" aria-label={t(titleKey)}>
        <ChartThemeProvider>
          <LineChart
            data={chartData}
            height={240}
            yAxisTitle="CSAT"
            yMinValue={0}
            yMaxValue={5}
            tickValues={xAxisTickValues}
            customDateTimeFormatter={(d: Date) => monthLabels.get(d.getTime()) ?? ''}
            enableReflow
          />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
}
